import React from 'react'
import { User, Users, Heart, Settings } from 'lucide-react'
import { useProfile } from './Hooks/useProfile'
import { LoadingSpinner } from './Components/UI/LoadingSpinner'
import { ErrorState } from './Components/UI/ErrorState'
import { TabNavigation } from './Components/UI/TabNavigation'
import { ProfileHeader } from './Components/ProfileHeader'
import { ContactInfoCard } from './Components/ContactInfoCard'
import { ReferralCard } from './Components/ReferralCard'
import { ReferralStatsCards } from './Components/ReferralStatsCards'
import { ReferralsList } from './Components/ReferralsList'
import { SavedPropertiesGrid } from './Components/SavedPropertiesGrid'
import { SettingsTab } from './Components/SettingsTab'
import { PROFILE_TABS } from './Common/profileConstants'
import { useNavigate } from 'react-router-dom'

export default function ProfilePage() {
  const navigate = useNavigate()
  const {
    // State
    user,
    loading,
    error,
    activeTab,
    editMode,
    formData,
    updateLoading,
    updateSuccess,
    updateError,
    referralStats,
    referrals,
    referralRewards,
    referralCode,
    referralLoading,
    copied,
    copiedReferrals,
    shareError,
    savedProperties,
    savedPropertiesLoading,
    
    
    // Actions
    setActiveTab,
    setEditMode,
    handleChange,
    handleSubmit,
    handleLogout,
    copyReferralCode,
    copyReferralCodeReferrals,
    handleShare,
    handleSavedProperty
  } = useProfile()

  // Tab configuration
  const tabs = [
    { id: PROFILE_TABS.OVERVIEW, label: 'Overview', icon: User },
    { id: PROFILE_TABS.REFERRALS, label: 'Referrals', icon: Users },
    { id: PROFILE_TABS.SAVED, label: 'Saved Properties', icon: Heart },
    { id: PROFILE_TABS.SETTINGS, label: 'Settings', icon: Settings }
  ]

  // Loading state
  if (loading) {
    return <LoadingSpinner text="Loading your profile..." />
  }

  // Error state
  if (error) {
    return (
      <ErrorState
        title="Error Loading Profile"
        message={error}
        onRetry={() => navigate('/login')}
        retryText="Go to Login"
      />
    )
  }

  // No user state
  if (!user) {
    return (
      <ErrorState
        title="No Profile Found"
        message="Please log in to view your profile."
        onRetry={() => navigate('/login')}
        retryText="Go to Login"
      />
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-teal-400/10 to-emerald-400/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-blue-400/10 to-teal-400/5 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-22 relative z-10">
        {/* Profile Header */}
        <ProfileHeader 
          user={user} 
          referralStats={referralStats} 
          onLogout={handleLogout} 
        />

        {/* Tab Navigation */}
        <TabNavigation 
          tabs={tabs} 
          activeTab={activeTab} 
          onTabChange={setActiveTab} 
        />

        {/* Tab Content */}
        <div className="space-y-8">
          {/* Overview Tab */}
          {activeTab === PROFILE_TABS.OVERVIEW && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Contact Information */}
              <div className="lg:col-span-2 space-y-6">
                <ContactInfoCard
                  user={user}
                  editMode={editMode}
                  formData={formData}
                  updateLoading={updateLoading}
                  updateSuccess={updateSuccess}
                  updateError={updateError}
                  onEdit={() => setEditMode(true)}
                  onChange={handleChange}
                  onSave={handleSubmit}
                  onCancel={() => setEditMode(false)}
                />
              </div>

              {/* Referral Card */}
              <div className="space-y-6">
                <ReferralCard
                  referralCode={referralCode}
                  userReferralCode={user?.referral_code}
                  onCopy={copyReferralCode}
                  onShare={handleShare}
                  copied={copied}
                  shareError={shareError}
                />
              </div>
            </div>
          )}

          {/* Referrals Tab */}
          {activeTab === PROFILE_TABS.REFERRALS && (
            <div className="space-y-8">
              {referralLoading ? (
                <LoadingSpinner text="Loading referral data..." />
              ) : (
                <>
                  {/* Stats Cards */}
                  <ReferralStatsCards referralStats={referralStats} />

                  {/* Recent Referrals */}
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-200/50 overflow-hidden">
                    <div className="p-6 border-b border-gray-200/50">
                      <div className="flex items-center justify-between">
                        <h3 className="text-xl font-bold text-gray-900">Recent Referrals</h3>
                      </div>
                    </div>
                    <div className="p-6">
                      <ReferralsList referrals={referrals} onInvite={handleShare} />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Saved Properties Tab */}
          {activeTab === PROFILE_TABS.SAVED && (
            <div className="space-y-8">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Saved Properties</h2>
                <a
                  href="/"
                  className="flex items-center gap-2 px-6 py-3 bg-teal-500 text-white rounded-xl hover:bg-teal-600 transition-all duration-200 font-medium"
                >
                  Browse Properties
                </a>
              </div>
              <SavedPropertiesGrid properties={savedProperties} loading={savedPropertiesLoading} savepropertyhandler ={handleSavedProperty}/>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === PROFILE_TABS.SETTINGS && <SettingsTab />}
        </div>
      </main>
    </div>
  )
}